import { useState } from "react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { BRAIN_COLOR_PALETTE, getNextAvailableColor } from "@/lib/constants/colors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateBrain } from "@/lib/query/hooks";
import { useToast } from "@/hooks/use-toast";
import { useColonyContext } from "@/hooks/useColonyContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const AddBrain = () => {
  const navigate = useNavigate();
  const { colonyId } = useParams<{ colonyId: string }>();
  const { activeColonyId } = useColonyContext();
  const { toast } = useToast();
  
  const [name, setName] = useState("");
  const [personality, setPersonality] = useState("");
  const [selectedModelId, setSelectedModelId] = useState<string>("gemini-flash-life");
  const [selectedModelLabel, setSelectedModelLabel] = useState<string>("Gemini 2.5 Flash Life (Free)");
  const usedColors: string[] = [];
  const [selectedColor, setSelectedColor] = useState<string>(getNextAvailableColor(usedColors));
  // Determine subscription tier from local cache (free/pro)
  const savedUserStr = typeof window !== 'undefined' ? localStorage.getItem('lotteryOfLife_user') : null;
  const subscriptionTier = (() => {
    try { return savedUserStr ? (JSON.parse(savedUserStr).subscriptionTier || 'free') : 'free'; } catch { return 'free'; }
  })();

  const modelOptions: Array<{ id: string; label: string; tier: 'free' | 'pro'; }> = [
    { id: 'gemini-flash-life', label: 'Gemini 2.5 Flash Life (Free)', tier: 'free' },
    { id: 'gemini-flash',      label: 'Gemini 2.5 Flash (Pro)',       tier: 'pro'  },
    { id: 'gemini-2.5-pro',    label: 'Gemini 2.5 Pro (Pro)',         tier: 'pro'  },
    { id: 'openai-gpt-5',      label: 'OpenAI GPT‑5 (Pro)',           tier: 'pro'  },
    // optionally: { id: 'xai-grok-2', label: 'xAI Grok‑2 (Pro)', tier: 'pro' }
  ];

  const handleSelectModel = (id: string) => {
    const option = modelOptions.find(o => o.id === id);
    if (!option) return;
    if (option.tier === 'pro' && subscriptionTier !== 'pro') {
      toast({
        title: 'Pro model',
        description: 'Upgrade to Pro to use this model.',
      });
      return;
    }
    setSelectedModelId(option.id);
    setSelectedModelLabel(option.label);
  };
  
  // Use colonyId from URL or active colony
  const effectiveColonyId = colonyId || activeColonyId;
  const createBrainMutation = useCreateBrain();

  const handleCreate = async () => {
    console.debug('[AddBrain] Creating brain with', {
      colonyId: effectiveColonyId,
      name,
      personalityPreview: personality.slice(0, 50),
      model: selectedModelId
    });
    let targetColonyId = effectiveColonyId;
    if (!targetColonyId) {
      try {
        const { api } = await import("@/lib/api/client");
        const cols = await api.getColonies();
        const list = Array.isArray(cols) ? cols : (Array.isArray((cols as any)?.colonies) ? (cols as any).colonies : []);
        targetColonyId = list?.[0]?.id;
      } catch {}
      if (!targetColonyId) {
        toast({ title: "Error", description: "No colony available.", variant: "destructive" });
        return;
      }
    }

    if (!name.trim() || !personality.trim()) {
      toast({
        title: "Error", 
        description: "Please fill in both name and personality fields.",
        variant: "destructive"
      });
      return;
    }

    // 🚨 TOKEN COST CONTROL: Limit personality length
    const MAX_PERSONALITY_LENGTH = 500; // ~125 tokens
    if (personality.trim().length > MAX_PERSONALITY_LENGTH) {
      toast({
        title: "Personality Too Long", 
        description: `Please keep personality under ${MAX_PERSONALITY_LENGTH} characters to control token costs. Current: ${personality.length}`,
        variant: "destructive"
      });
      return;
    }

    try {
      await createBrainMutation.mutateAsync({
        colonyId: targetColonyId,         // ✅ Pass resolved colony ID
        name: name.trim(),
        personality: personality.trim(), // 🎭 THE ROLEPLAY DESCRIPTION!
        modelPreference: selectedModelId,
        color: selectedColor,
        tone: "professional", // Default tone
        humor: "light",       // Default humor
        specialties: []       // Could be extracted from personality later
      });

      toast({
        title: "Success",
        description: `${name} has been added to your colony!`
      });

      navigate(`/chat/${targetColonyId}`);
    } catch (error) {
      console.error("Failed to create brain:", error);
      toast({
        title: "Error",
        description: "Failed to create brain. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-background safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ios-separator bg-ios-surface">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate("/")}
          className="text-foreground hover:bg-ios-surface-secondary w-11 h-11 rounded-full"
        >
          <ChevronLeft size={22} />
        </Button>
        <h1 className="ios-title text-foreground">Add Brain</h1>
        <div className="w-11" />
      </div>

      {/* Form Content */}
      <div className="flex-1 px-4 py-6 space-y-6 pb-24">
        {/* Name Input */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Name</label>
          <Input 
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name"
            className="h-12 bg-ios-surface-secondary border-ios-separator text-foreground placeholder:text-muted-foreground rounded-xl"
          />
        </div>

        {/* Color Picker */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Color</label>
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-medium"
              style={{ backgroundColor: selectedColor }}
            >
              {name ? name.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="grid grid-cols-8 gap-2">
              {BRAIN_COLOR_PALETTE.map(c => (
                <button
                  key={c.value}
                  onClick={() => setSelectedColor(c.value)}
                  className={`w-7 h-7 rounded-lg ${selectedColor === c.value ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}
                  style={{ backgroundColor: c.value }}
                  title={c.name}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Personality Input */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-muted-foreground">Personality & Backstory</label>
            <span className={`text-xs ${personality.length > 400 ? 'text-red-500' : personality.length > 300 ? 'text-yellow-500' : 'text-muted-foreground'}`}>
              {personality.length}/500
            </span>
          </div>
          <Textarea 
            value={personality}
            onChange={(e) => setPersonality(e.target.value)}
            placeholder="Describe the brain's traits (e.g., 'You are Dr. Sarah Chen, a behavioral economist...')"
            className="min-h-[120px] bg-ios-surface-secondary border-ios-separator text-foreground placeholder:text-muted-foreground resize-none rounded-xl"
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground">
            💡 Tip: Be specific but concise. This text is sent with every message to maintain personality.
          </p>
        </div>

        {/* Model Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Model</label>
          <div className="relative">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  className="w-full h-12 bg-ios-surface-secondary border-ios-separator text-left justify-between hover:bg-ios-surface-tertiary rounded-xl"
                >
                  <span className="text-foreground">{selectedModelLabel}</span>
                  <ChevronRight size={18} className="text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                {modelOptions.map((opt) => (
                  <DropdownMenuItem
                    key={opt.id}
                    disabled={opt.tier === 'pro' && subscriptionTier !== 'pro'}
                    onClick={() => handleSelectModel(opt.id)}
                    className="flex items-center justify-between"
                  >
                    <span className={opt.tier === 'pro' && subscriptionTier !== 'pro' ? 'text-muted-foreground' : ''}>{opt.label}</span>
                    {selectedModelId === opt.id && <Check size={16} />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Create Button */}
        <div className="pt-8">
          <Button 
            className="w-full h-12 bg-ios-blue text-white hover:bg-ios-blue/90 font-medium rounded-xl disabled:opacity-50"
            onClick={handleCreate}
            disabled={!name.trim() || !personality.trim() || createBrainMutation.isPending}
          >
            Create
          </Button>
        </div>
      </div>

      {/* Bottom tabs are provided globally; no local bottom nav here */}
    </div>
  );
};

export default AddBrain;